import * as React from "react";
import { toast } from "react-toastify";
import { useDispatch } from "redux-react-hook";
import _ from "lodash";
import { useForm } from "react-hook-form";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Switch } from "src/library/components/Forms";
import { Text } from "src/library/components/Text";
import { CascadeSettingFieldLabel } from "src/library/components/Forms/CascadeSettingField";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import { useInheritedSettingsApi } from "src/core/shared/api/inheritedSettings.api";
import {
  IInheritedSetting,
  InheritedSettingValue,
  OrderedInheritedSettings
} from "src/core/drops/api/drops.api";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { useCreditReasonCodes } from "src/modules/credit-request/hooks/useCreditReasonCodes";
import {
  InheritedRadioRow,
  CreditRequestAutoApprovalThresholds,
  InheritedSettingsWrapper,
  InheritedSettingsField
} from "src/core/shared/components/Inherited-settings";

type IGlobalSettingFormFields = { [P in IFormFields]: IFormValue<IFormFields> };
type IFormFields = keyof Pick<InheritedSettingValue, "credit_request_auto_approval">;

interface IFormValue<T> {
  name: T;
  inherited: boolean;
  value?: any;
  Description?: JSX.Element;
  SettingElement: (setting: IFormValue<T>) => React.ReactNode;
  hidden?: boolean;
}

type IFormValues = { [P in IFormFields]?: InheritedSettingValue[IFormFields] };

/**
 * Credit request settings page
 */
const CreditRequestSettings: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology } = props;
  const checkPermission = useCheckUserPermission();
  const dispatch = useDispatch();
  const creditRequestReasons = useCreditReasonCodes();
  const [ getSettingsRes, getSettingsReq ] = useInheritedSettingsApi("GET_GLOBAL_SETTINGS");
  const [ updateSettingRes, updateSettingReq ] = useInheritedSettingsApi("UPDATE_SETTING");
  const [ globalSettings, setGlobalSettings ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const canAmend = React.useMemo(() => checkPermission("amend-global-settings"), []);
  const [ formValues, setFormValues ] = React.useState<IFormValues>();
  const isLoading = React.useMemo(() => updateSettingRes.pending, [ updateSettingRes.pending ]);
  const [ updatingSettings, setUpdatingSettings ] = React.useState<{ [P in IFormFields]?: "not-changed" | "required" | "loading" }>({ credit_request_auto_approval: "not-changed" });
  const { handleSubmit } = useForm<IGlobalSettingFormFields>();

  /**
   * Compile the settings
   */
  const settings: IGlobalSettingFormFields | null = React.useMemo(() => {
    // Return null if there are no settings found
    if (!globalSettings) {
      return null;
    }

    return {
      credit_request_auto_approval: {
        name: "credit_request_auto_approval",
        inherited: true,
        value: globalSettings && globalSettings.credit_request_auto_approval?.value ? globalSettings.credit_request_auto_approval.value : { enabled: false },
        Description: (
          <>
            <Text
              as="h2"
              fontSize="h3"
            >
              Credit request auto-approval
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This sets whether a ${terminology.customer.singular.text} credit requests can be automatically approved based on the given criteria.`}
            </Text>
          </>
        ),
        SettingElement: setting => (
          <>
            <InheritedRadioRow direction="horizontal">
              <Switch
                disabled={!canAmend}
                onChange={checked => {
                  // Update this field value
                  setFormValues(current => {
                    const parsedCurrent = current || {};

                    return {
                      ...parsedCurrent,
                      credit_request_auto_approval: {
                        ...parsedCurrent[ "credit_request_auto_approval" ],
                        enabled: checked
                      }
                    };
                  });
                  // Update the updatingSettings state
                  setUpdatingSettings({ credit_request_auto_approval: "required" });
                }}
                checked={formValues?.credit_request_auto_approval?.enabled || false}
              />

              <CascadeSettingFieldLabel
                for={`${setting.name}-default`}
                label={`Auto-approve ${terminology.customer.singular.text} credit requests`}
              />
            </InheritedRadioRow>

            <CreditRequestAutoApprovalThresholds
              data={formValues?.credit_request_auto_approval}
              reasonData={creditRequestReasons}
              onChange={data => {
                // Update this field value
                setFormValues(current => {
                  const parsedCurrent = current || {};

                  return {
                    ...parsedCurrent,
                    credit_request_auto_approval: data
                  };
                });
              }}
            />
          </>
        )
      }
    };
  // Only update if the given dependencies update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canAmend,
    globalSettings,
    formValues
  ]);

  /**
   * Handle submit settings
   */
  const handleSubmitSettings: React.FormEventHandler<HTMLFormElement> = React.useCallback(() => {
    _.each(formValues, (settingValue, settingName) => {
      if (updatingSettings[ settingName ] === "required" && globalSettings) {
        updateSettingReq({
          ...globalSettings[ settingName ],
          value: settingValue
        });
      }
    });
  }, [
    formValues,
    globalSettings,
    updateSettingReq,
    updatingSettings
  ]);

  // On mount
  React.useEffect(() => {
    getSettingsReq({});
  }, [ getSettingsReq ]);

  // On getSettingsRes change
  React.useEffect(() => {
    // On error
    if (getSettingsRes.error) {
      dispatch(setGlobalError(getSettingsRes.error));
    }

    // On success
    if (getSettingsRes.data) {
      const settings: OrderedInheritedSettings<IInheritedSetting> = {};
      const formValues: IFormValues = {};

      _.each(getSettingsRes.data, setting => {
        settings[ setting.name ] = setting;

        switch (setting.name) {
          case "credit_request_auto_approval":
            // @ts-ignore - Functionality correct
            formValues[ setting.name ] = setting.value;
            break;
        }
      });
      setGlobalSettings(settings);
      setFormValues(formValues);
    }
  }, [
    dispatch,
    getSettingsRes.error,
    getSettingsRes.data
  ]);

  // On updateSettingRes change
  React.useEffect(() => {
    // On failure
    if (updateSettingRes.error) {
      toast.error(updateSettingRes.error.message);
    }

    // On success
    if (updateSettingRes.data) {
      toast.success("Settings saved!");
    }
  }, [ updateSettingRes ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Grid.Bounds direction="vertical">
          {!settings ? (
            <LoadingSpinner
              type="circular"
              offsetTop={50}
            />
          ) : (
            <form onSubmit={handleSubmit(handleSubmitSettings)}>
              {_.map(settings, (setting, i) => {
                if (setting.hidden) {
                  return null;
                }

                return (
                  <InheritedSettingsWrapper key={`setting-${i}`}>
                    <InheritedSettingsField
                      disabled={updatingSettings && updatingSettings[ setting.name ] === "loading"}
                      id={`${setting.name}-${i}`}
                      key={setting.name}
                    >
                      {setting.Description}

                      {setting.SettingElement(setting)}
                    </InheritedSettingsField>
                  </InheritedSettingsWrapper>
                );
              })}

              {canAmend && (
                <Button
                  loading={isLoading}
                  disabled={isLoading}
                  type="submit"
                >
                  Save
                </Button>
              )}
            </form>
          )}
        </Grid.Bounds>
      </MainContentWrapper>
    </Layout>
  );
};

export default CreditRequestSettings;
